import { type FC } from 'react';
import type { RecommenderProps, RecommenderStep } from './types';
import type { ExitStrategyProps } from '../ExitStrategy/types';
import type { PiiConfirmationProps } from '../PiiConfirmation/types';
import type { ProgramSelectProps } from '../ProgramSelect/types';
import type { AdditionalQuestionsProps } from '../AdditionalQuestions/types';
import type { SubmissionProps } from '../Submission/types';
import { resolveApplicationCtaHref } from '../../utils/resolveApplicationCtaHref';

type Props = {
  programSelectComponent: FC<ProgramSelectProps>;
  PiiConfirmationComponent: FC<PiiConfirmationProps>;
  AdditionalQuestionsComponent: FC<AdditionalQuestionsProps>;
  ExitStrategyComponent: FC<ExitStrategyProps>;
  SubmissionComponent: FC<SubmissionProps>;
} & RecommenderProps;

const Recommender: FC<Props> = ({
  results,
  selectedResult,
  startTime,
  onCloseExperience,
  step,
  programSelectComponent,
  PiiConfirmationComponent,
  AdditionalQuestionsComponent,
  ExitStrategyComponent,
  SubmissionComponent,
  onBackButtonClick,
  firstName,
  lastName,
  email,
  phone,
  formErrors,
  onProgramClick,
  onProgramSubmit,
  setFirstName,
  setLastName,
  setEmail,
  setPhone,
  setFormErrors,
  canSubmit,
  isLoading,
  leadSubmissionStatus,
  setLeadSubmissionStatus,
  piiLeadErrors,
  productClickedCorrelationId,
  questions,
  editMode,
  setEditMode,
  submittedResults,
  partnerIdentifiers,
  extraCtaConfig,
}) => {
  const stepMap: Record<
    RecommenderStep,
    | typeof programSelectComponent
    | typeof PiiConfirmationComponent
    | typeof AdditionalQuestionsComponent
    | typeof ExitStrategyComponent
    | typeof SubmissionComponent
  > = {
    PROGRAM_SELECT: programSelectComponent,
    PII_CONFIRMATION: PiiConfirmationComponent,
    ADDITIONAL_QUESTIONS: AdditionalQuestionsComponent,
    EXIT_STRATEGY: ExitStrategyComponent,
    SUBMISSION: SubmissionComponent,
  };

  const StepComponent = stepMap[step as keyof typeof stepMap];

  if (!StepComponent) return null;

  const leadForCta = {
    firstName,
    lastName,
    email,
    phoneNumber: phone,
  };

  const stepProps: Record<string, any> = {
    isLoading,
    canSubmit,
    selectedResult,
    introText: selectedResult?.confirmationShortIntroText || '',
    valueProps: selectedResult?.valueProps,
    programName: selectedResult?.programName || '',
    schoolName: selectedResult?.schoolName || '',
    schoolSlug: selectedResult?.schoolSlug || '',
    city: selectedResult?.city,
    state: selectedResult?.state,
    applicationCtaUrl: resolveApplicationCtaHref(selectedResult, {
      lead: leadForCta,
      partnerIdentifiers,
    }),
    applicationCtaLabel: selectedResult?.applicationCtaLabel,
    applicationCtaFieldMap: selectedResult?.applicationCtaFieldMap,
    startTime,
    onBackButtonClick,
    onProgramClick,
    onProgramSubmit,
    importantFactors: [],
    results,
    piiLeadErrors: piiLeadErrors || [],
    editMode,
    setEditMode,
    setPiiLeadErrors: () => {},
    setFirstName,
    setLastName,
    setEmail,
    setPhone,
    firstName,
    lastName,
    email,
    phone,
    formErrors,
    setFormErrors,
    onCloseExperience,
    leadSubmissionStatus,
    setLeadSubmissionStatus,
    productClickedCorrelationId: productClickedCorrelationId || '',
    questions,
    onFormError: () => {},
    formCorrelationId: '',
    tcpa: selectedResult?.tcpa || '',
    programFlow: selectedResult?.programFlow || [],
    tcpaSecondParagraph: selectedResult?.tcpaSecondParagraph || '',
    submittedResults,
    extraCta: extraCtaConfig
      ? {
          enabled: extraCtaConfig.enabled,
          label: extraCtaConfig.label,
          destination: selectedResult?.postLeadCta,
        }
      : undefined,
    partnerIdentifiers,
    ...((extraCtaConfig && selectedResult?.postLeadCta?.url) ||
    selectedResult?.applicationCtaFieldMap
      ? { lead: leadForCta }
      : {}),
  };

  const Component = StepComponent as FC<Record<string, any>>;
  return <Component {...stepProps} />;
};

export default Recommender;
